import { useCallback } from 'react';
import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	Store,
	StoreActionApi,
} from 'react-sweet-state';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

type MenuId = string;

type FlyoutState = {
	openFlyoutId: MenuId | null;
};

const initialState: FlyoutState = {
	openFlyoutId: null, // Keeps track of the currently open flyout by its menuId
};

export const FlyoutStore: Store<
	FlyoutState,
	{
		openFlyout: (menuId: MenuId) => ({ setState }: StoreActionApi<FlyoutState>) => void;
		closeFlyout: (
			menuId?: MenuId | null,
		) => ({ setState, getState }: StoreActionApi<FlyoutState>) => void;
	}
> = createStore({
	name: 'FlyoutStore',
	initialState,
	actions: {
		openFlyout:
			(menuId: MenuId): (({ setState }: StoreActionApi<FlyoutState>) => void) =>
			({ setState }) => {
				setState({ openFlyoutId: menuId });
			},
		closeFlyout:
			(
				menuId: MenuId | null = null,
			): (({ setState, getState }: StoreActionApi<FlyoutState>) => void) =>
			({ setState, getState }) => {
				if (!menuId || menuId === getState().openFlyoutId) {
					setState({ openFlyoutId: null });
				}
			},
	},
});

export const useIsFlyoutOpen: HookFunction<
	boolean,
	BoundActions<
		FlyoutState,
		{
			openFlyout: (menuId: MenuId) => ({ setState }: StoreActionApi<FlyoutState>) => void;
			closeFlyout: (
				menuId?: MenuId | null,
			) => ({ setState, getState }: StoreActionApi<FlyoutState>) => void;
		}
	>,
	string | undefined
> = createHook(FlyoutStore, {
	selector: (state: FlyoutState, menuId: MenuId | undefined) => state.openFlyoutId === menuId,
});

export const useFlyout = (
	menuId: MenuId,
): {
	isFlyoutOpen: boolean;
	openFlyout: () => void;
	closeFlyout: () => void;
} => {
	const [isFlyoutOpen, flyoutActions] = useIsFlyoutOpen(menuId);
	const openFlyout = useCallback(
		(): void => flyoutActions.openFlyout(menuId),
		[flyoutActions, menuId],
	);
	const closeFlyout = useCallback(
		(): void => flyoutActions.closeFlyout(menuId),
		[flyoutActions, menuId],
	);

	return { isFlyoutOpen, openFlyout, closeFlyout };
};

export const useFlyoutActions: HookActionsFunction<
	BoundActions<
		FlyoutState,
		{
			openFlyout: (menuId: MenuId) => ({ setState }: StoreActionApi<FlyoutState>) => void;
			closeFlyout: (
				menuId?: MenuId | null,
			) => ({ setState, getState }: StoreActionApi<FlyoutState>) => void;
		}
	>
> = createActionsHook(FlyoutStore);
